import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';

import _ from 'lodash';

import {
    StyledCard,
    StyledCardContent,
    Text,
    OutlinedButton,
    Avatar,
    Title,
    StyledFormHelperText,
    TimePicker,
    CreditsIcon,
    FilledIconButton,
    DeleteIcon2,
    HorizontalRule,
    Select,
    Span,
} from '../../index';

import { StyledPatientContainer, StyledPatientListContainer } from './style';

import AddPatientDialog from '../Dialog/AddPatientDialog';

const LAYOUT = Object.freeze({
    ONE_ON_ONE: 'ONE_ON_ONE',
    GROUP: 'GROUP',
    OPEN: 'OPEN',
    NEXT_GROUP: 'NEXT_GROUP',
});

const PatientCard = ({
    errors,
    selectedPatients,
    handleAddPatient,
    handlePatientTimeChange,
    handleRemovePatient,
    loading,
    layout,
    numberOfParticipants,
    handleNumberOfParticipantsChange,
    options,
}) => {
    const [open, setOpen] = useState(false);

    return (
        <StyledCard style={{ position: 'relative' }}>
            <AddPatientDialog
                open={open}
                selectedPatients={selectedPatients}
                onAdd={handleAddPatient}
                options={options}
                onCancel={() => setOpen(false)}
                onClose={() => setOpen(false)}
                max={2}
            />
            <StyledCardContent style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Text $fontSize="20px" $bold>
                        {LAYOUT[layout] === 'OPEN' ? 'Participant' : 'Client'}
                    </Text>
                    {LAYOUT[layout] !== 'OPEN' && (
                        <OutlinedButton
                            $width="158px"
                            $height="32px"
                            $noShadow
                            onClick={() => setOpen(true)}
                            disabled={loading}
                        >
                            + Add Client
                        </OutlinedButton>
                    )}
                </div>
                {LAYOUT[layout] === 'OPEN' && (
                    <div>
                        <div style={{ width: '300px', marginBottom: '10px' }}>
                            <Select
                                label={<Span $color="black">Number of participant</Span>}
                                name="numberOfParticipants"
                                placeholder="Please select"
                                value={numberOfParticipants}
                                onChange={handleNumberOfParticipantsChange}
                                options={Array.from({ length: 100 }, (x, i) => ({
                                    value: i + 1,
                                    text: i + 1,
                                }))}
                            />
                        </div>
                        <HorizontalRule />
                    </div>
                )}
                {!_.isEmpty(selectedPatients) && (
                    <StyledPatientListContainer>
                        {selectedPatients.map((patient) => (
                            <StyledPatientContainer layout={LAYOUT[layout]} key={patient.id}>
                                <Avatar
                                    alt={`${patient?.user?.fullname}`}
                                    src={patient?.user?.avatar ? patient.user?.avatar?.url : null}
                                    $avatarSize="48px"
                                />
                                <Title
                                    style={{
                                        margin: '4px 0 8px',
                                        width: '200px',
                                    }}
                                    $fontSize="16px"
                                    $ellipsis
                                    $lineHeight="20px"
                                >{`${patient?.user?.fullname}`}</Title>
                                {LAYOUT[layout] !== 'NEXT_GROUP' && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Text>{patient?.HNCode}</Text>
                                    </div>
                                )}

                                {LAYOUT[layout] !== 'ONE_ON_ONE' && <div />}
                                {LAYOUT[layout] === 'ONE_ON_ONE' && (
                                    <Fragment>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'grid',
                                                    alignItems: 'center',
                                                    gridTemplateColumns:
                                                        'repeat(2, minmax(120px,150px))',
                                                    columnGap: '15px',
                                                }}
                                            >
                                                <TimePicker
                                                    id="patientStartTime"
                                                    label="Start time"
                                                    placeholder="Select"
                                                    value={patient.startTime}
                                                    onChange={(time) =>
                                                        handlePatientTimeChange(
                                                            patient.id,
                                                            'startTime',
                                                            time
                                                        )
                                                    }
                                                    disabled={patient.disabled || loading}
                                                />

                                                <TimePicker
                                                    id="patientEndTime"
                                                    label="End time"
                                                    placeholder="Select"
                                                    value={patient.endTime}
                                                    onChange={(time) =>
                                                        handlePatientTimeChange(
                                                            patient.id,
                                                            'endTime',
                                                            time
                                                        )
                                                    }
                                                    disabled={patient.disabled || loading}
                                                />
                                            </div>
                                            {errors.find(
                                                (value) =>
                                                    value.property === `${patient.id}-startTime`
                                            ) && (
                                                <StyledFormHelperText error>
                                                    {
                                                        errors.find(
                                                            (value) =>
                                                                value.property ===
                                                                `${patient.id}-startTime`
                                                        )?.message
                                                    }
                                                </StyledFormHelperText>
                                            )}
                                            {errors.find(
                                                (value) =>
                                                    value.property === `${patient.id}-endTime`
                                            ) && (
                                                <StyledFormHelperText error>
                                                    {
                                                        errors.find(
                                                            (value) =>
                                                                value.property ===
                                                                `${patient.id}-endTime`
                                                        )?.message
                                                    }
                                                </StyledFormHelperText>
                                            )}
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                columnGap: '5px',
                                            }}
                                        >
                                            <CreditsIcon />
                                            <Text>{patient.credit}</Text>
                                        </div>
                                    </Fragment>
                                )}

                                <FilledIconButton
                                    style={{
                                        justifySelf: 'end',
                                    }}
                                    $backgroundColor="gray6"
                                    onClick={() =>
                                        handleRemovePatient(patient.id, patient?.sessionId)
                                    }
                                    disabled={patient.disabled || loading}
                                >
                                    <DeleteIcon2 fill="gray2" />
                                </FilledIconButton>
                            </StyledPatientContainer>
                        ))}
                    </StyledPatientListContainer>
                )}
                {LAYOUT[layout] === 'OPEN' && (
                    <div style={{ width: '100%' }}>
                        <div
                            style={{
                                margin: '10px auto 30px',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <OutlinedButton
                                $width="auto"
                                $height="32px"
                                $noShadow
                                onClick={() => setOpen(true)}
                                disabled={loading}
                            >
                                + Invite client
                            </OutlinedButton>
                        </div>
                        {errors.find((value) => value.property === `numberOfParticipants`) && (
                            <StyledFormHelperText error>
                                {
                                    errors.find(
                                        (value) => value.property === `numberOfParticipants`
                                    )?.message
                                }
                            </StyledFormHelperText>
                        )}
                        <div>
                            <Text $fontSize="14px" $color="gray2">
                                * The system will send a notification to the client that the
                                provider invited to attend the session.
                            </Text>
                        </div>
                    </div>
                )}
            </StyledCardContent>
            {errors.find((value) => value.property === `selectedPatients`) && (
                <StyledFormHelperText error>
                    {errors.find((value) => value.property === `selectedPatients`)?.message}
                </StyledFormHelperText>
            )}
        </StyledCard>
    );
};

PatientCard.propTypes = {
    errors: PropTypes.arrayOf(PropTypes.object),
    selectedPatients: PropTypes.arrayOf(PropTypes.object),
    handleAddPatient: PropTypes.func,
    handlePatientTimeChange: PropTypes.func,
    handleRemovePatient: PropTypes.func,
    loading: PropTypes.bool,
    layout: PropTypes.oneOf(Object.keys(LAYOUT)),
    numberOfParticipants: PropTypes.number,
    handleNumberOfParticipantsChange: PropTypes.func,
};

export default PatientCard;
